import { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import type { AssessmentStatusResponse } from '../types/api';
import { getAssessmentStatus } from '../services/api';

interface UseAssessmentStatusResult {
  status: AssessmentStatusResponse | null;
  isLoading: boolean;
  error: string | null;
}

export function useAssessmentStatus(assessmentId: string): UseAssessmentStatusResult {
  const [status, setStatus] = useState<AssessmentStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let mounted = true;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const fetch = async () => {
      try {
        const data = await getAssessmentStatus(assessmentId);
        if (mounted) {
          setStatus(data);
          setError(null);
          setIsLoading(false);
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
            stopPolling();
          }
        }
      } catch (err) {
        if (!mounted) return;
        const msg = describeError(err);
        setError(msg);
        setIsLoading(false);
        if (isHardError(err)) {
          stopPolling();
        }
      }
    };

    fetch();
    intervalRef.current = setInterval(fetch, 2_000);

    return () => {
      mounted = false;
      stopPolling();
    };
  }, [assessmentId]);

  return { status, isLoading, error };
}

function describeError(err: unknown): string {
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    const code =
      typeof detail === 'string' ? detail
        : detail && typeof detail === 'object' && 'error' in detail ? String((detail as { error: unknown }).error)
        : null;
    if (status === 404 && code === 'ASSESSMENT_NOT_FOUND') {
      /* Sprint 5 fix — surfaced as raw "Failed to load assessment
         status (404): ASSESSMENT_NOT_FOUND" before. Translate the
         common 404 code into actionable wording so the analyst knows
         the row vanished (re-run, deleted, migrated) and that
         refreshing the list is the right next step. */
      return (
        'Assessment no longer exists. It may have been deleted, ' +
        'replaced by a re-run, or removed during a database migration. ' +
        'Refresh the Assessments list and try the most recent entry.'
      );
    }
    if (status === 404 && code === 'ANALYSIS_NOT_FOUND') {
      return 'Analysis not yet available — workflow may still be running. Wait 10-30 seconds and reload.';
    }
    if (status === 404) return 'Assessment not found';
    if (status === 401 || status === 403) return 'Not authorised to view this assessment';
    if (status && code) return `Failed to load assessment status (${status}): ${code}`;
    if (status) return `Failed to load assessment status (HTTP ${status})`;
    if (err.code === 'ERR_NETWORK') return 'Backend unreachable — check the API service';
  }
  return 'Failed to load assessment status';
}

function isHardError(err: unknown): boolean {
  if (err instanceof AxiosError) {
    const s = err.response?.status;
    return s === 401 || s === 403 || s === 404;
  }
  return false;
}
